import React, { useEffect, useMemo, useRef, useState } from "react"
import { MapPin, User } from "lucide-react"
import Accordion from "./Accordion.jsx"
import InputField from "./InputField.jsx"
import CustomerSearchInput from "./CustomerSearchInput.jsx"
import CustomerChoicePrompt from "./CustomerChoicePrompt.jsx"
import { FieldChangeHint } from "./FieldChangeHint.jsx"
import { ContactService } from "../services/contactService.js"
import {
    shippingFieldsFromApiCustomer,
    shippingFieldsFromShipTo,
} from "./shippingAddressUtils.js"
import { useRegionOptions } from "./useRegionOptions.js"
import { useCountries } from "./useCountries.js"
import { isShippingAddressComplete } from "./sectionCompletion.js"
import { focusOrderFormNextButton } from "./orderFormFocusUtils.js"
import {
    ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
} from "./orderFormAccordionConstants.js"
import { focusElement } from "../utils/scrollFocusedElement.js"
import { NAME_COMMA_ERROR, nameHasComma } from "./nameCommaValidation.js"
import {
    ADDRESS_PERSON_MIRROR_KEYS,
    addressHasAnyDetail,
    addressHasEdits,
    getAddressFieldDiffs,
    getAddressPersonFieldDiffs,
    snapshotAddressBaseline,
    undoAddressField,
} from "./customerFormBaseline.js"

function formatPhone(value) {
    return ContactService.formatPhone(value)
}

function contactLikeFromProps(contact) {
    return {
        firstName: contact.firstName ?? "",
        lastName: contact.lastName ?? "",
        companyName: contact.companyName ?? "",
        phone: contact.phone ?? "",
        email: contact.email ?? "",
    }
}

function contactPersonMirrorValues(contact) {
    const base = contactLikeFromProps(contact)
    return {
        ...base,
        phone: formatPhone(base.phone) || base.phone,
    }
}

/** Empty address fields; matches `shipping` initial state in NewOrderFormPage. */
function emptyManualShippingAddressFields() {
    return {
        firstName: "",
        lastName: "",
        companyName: "",
        email: "",
        country: "",
        province: "",
        city: "",
        streetAddress: "",
        postalCode: "",
        phone: "",
    }
}

function applyShippingCustomerLoad(prev, fields, shippingCustomerId, defaultShipAddressId) {
    const next = {
        ...prev,
        ...fields,
        shippingCustomerId,
        defaultShipAddressId,
        addressDirty: false,
        // Default "no" for update-default prompt if the user later edits.
        attachAddressToCustomer: false,
    }
    next.addressBaseline = snapshotAddressBaseline(next)
    return next
}

function clearShippingCustomer(prev) {
    return {
        ...prev,
        ...emptyManualShippingAddressFields(),
        shippingCustomerId: null,
        defaultShipAddressId: null,
        addressDirty: false,
        attachAddressToCustomer: true,
        addressBaseline: null,
    }
}

/** @param {Record<string, unknown>} v */
function firstMissingShippingFieldId(v) {
    if (String(v.country ?? "").trim() === "") {
        return "shipping-country"
    }
    if (String(v.firstName ?? "").trim() === "") {
        return "shipping-first-name"
    }
    if (String(v.lastName ?? "").trim() === "") {
        return "shipping-last-name"
    }
    if (String(v.province ?? "").trim() === "") {
        return "shipping-province"
    }
    if (String(v.city ?? "").trim() === "") {
        return "shipping-city"
    }
    if (String(v.streetAddress ?? "").trim() === "") {
        return "shipping-street"
    }
    if (String(v.postalCode ?? "").trim() === "") {
        return "shipping-postal"
    }
    if (String(v.phone ?? "").trim() === "") {
        return "shipping-phone"
    }
    return "shipping-country"
}

/**
 * @param {{
 *   value: Record<string, unknown>,
 *   delivery: { method: string, pickupLocationId: number | null },
 *   contact: Record<string, unknown> & {
 *     customerId?: number | null,
 *     default_ship_id?: number | null,
 *     ship_to?: unknown,
 *   },
 *   onChange: (v: Record<string, unknown> | ((p: Record<string, unknown>) => Record<string, unknown>)) => void,
 *   onContactChange?: (v: Record<string, unknown> | ((p: Record<string, unknown>) => Record<string, unknown>)) => void,
 *   onNext: () => void,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   isActive?: boolean,
 *   onJumpToSection?: (index: number) => void,
 *   activationFocusMode?: "first" | "nextRequired" | null,
 *   onActivationFocusConsumed?: () => void,
 *   activationFocusDelayMs?: number,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   hasErrors?: boolean,
 * }} props
 */
export default function ShippingSection({
    value,
    delivery,
    contact,
    onChange,
    onContactChange,
    onNext,
    title = "3. Shipping Information",
    isOpen,
    touched = false,
    statusLabel = null,
    disabled = false,
    onHeaderClick,
    isActive = false,
    onJumpToSection,
    activationFocusMode = null,
    onActivationFocusConsumed,
    activationFocusDelayMs = 350,
    fieldErrors = null,
    showFieldErrors = false,
    hasErrors = false,
}) {
    const nextButtonRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const [shippingSearchQuery, setShippingSearchQuery] = useState("")
    const [shippingSearchFocusNonce, setShippingSearchFocusNonce] = useState(0)
    const [emphasizeNext, setEmphasizeNext] = useState(false)
    const valueRef = useRef(value)
    valueRef.current = value
    const prevContactCustomerIdRef = useRef(/** @type {number | null} */(null))
    const prevContactSyncKeyRef = useRef(/** @type {string | null} */(null))
    /** Keys the user edited on shipping; stop contact→ship mirror for those. */
    const contactPersonOverwriteKeysRef = useRef(/** @type {Set<string>} */(new Set()))
    const softContactPersonMirrorActiveRef = useRef(false)
    const contactSyncKey =
        `${contact.customerId ?? ""}|${JSON.stringify(contact.ship_to ?? null)}|` +
        `${contact.firstName ?? ""}|${contact.lastName ?? ""}|${contact.email ?? ""}|${contact.phone ?? ""}|` +
        `${contact.companyName ?? ""}|${contact.default_ship_id ?? ""}`

    function resetContactPersonSoftMirror() {
        contactPersonOverwriteKeysRef.current = new Set()
        softContactPersonMirrorActiveRef.current = false
    }

    const { countries, countryNames } = useCountries()
    const regionOptions = useRegionOptions(String(value.country ?? ""), countries)

    const isCompleted = isShippingAddressComplete(value) && Boolean(touched)

    const mainCustomerDisplayLabel =
        String(contact.selectedCustomerLabel ?? "").trim() ||
        [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
        String(contact.email ?? "").trim() ||
        String(contact.phone ?? "").trim()

    const shippingAlignedWithMainCustomer = useMemo(() => {
        if (contact.customerId == null) {
            return false
        }
        if (value.shippingCustomerId == null) {
            return false
        }
        if (
            Number(value.shippingCustomerId) !== Number(contact.customerId)
        ) {
            return false
        }
        const q = shippingSearchQuery.trim().toLowerCase()
        const label = mainCustomerDisplayLabel.trim().toLowerCase()
        return q === label
    }, [
        contact.customerId,
        value.shippingCustomerId,
        shippingSearchQuery,
        mainCustomerDisplayLabel,
    ])

    const showResetToMainCustomer =
        contact.customerId != null && !shippingAlignedWithMainCustomer

    /** Bind shipping to contact’s customer when appropriate (main customer / first load). */
    useEffect(() => {
        if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
            return
        }
        const cid =
            contact.customerId != null ? Number(contact.customerId) : null
        const prevContact = prevContactCustomerIdRef.current

        if (cid == null) {
            if (prevContact != null) {
                resetContactPersonSoftMirror()
                onChange((prev) => clearShippingCustomer(prev))
            }
            prevContactCustomerIdRef.current = null
            return
        }

        const sid = valueRef.current.shippingCustomerId ?? null
        const sidNum = sid != null ? Number(sid) : null
        const wasOnMain = sidNum == null || sidNum === prevContact

        if (!wasOnMain) {
            prevContactCustomerIdRef.current = cid
            return
        }

        prevContactCustomerIdRef.current = cid
        let cancel = false
            ; (async () => {
                const fields = await shippingFieldsFromShipTo(
                    contact.ship_to ?? null,
                    contactLikeFromProps(contact)
                )
                if (cancel) {
                    return
                }
                onChange((prev) =>
                    applyShippingCustomerLoad(
                        prev,
                        fields,
                        cid,
                        contact.default_ship_id ?? null
                    )
                )
            })()
        return () => {
            cancel = true
        }
    }, [contact.customerId, delivery.method, delivery.pickupLocationId])

    /** While shipping tracks the main contact customer, keep fields in sync with contact. */
    useEffect(() => {
        if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
            return
        }
        if (contact.customerId == null) {
            return
        }
        if (
            Number(valueRef.current.shippingCustomerId) !==
            Number(contact.customerId)
        ) {
            prevContactSyncKeyRef.current = null
            return
        }
        if (prevContactSyncKeyRef.current === contactSyncKey) {
            return
        }
        prevContactSyncKeyRef.current = contactSyncKey
        let cancel = false
            ; (async () => {
                const fields = await shippingFieldsFromShipTo(
                    contact.ship_to ?? null,
                    contactLikeFromProps(contact)
                )
                if (cancel) {
                    return
                }
                onChange((prev) =>
                    applyShippingCustomerLoad(
                        prev,
                        fields,
                        contact.customerId ?? null,
                        contact.default_ship_id ?? null
                    )
                )
            })()
        return () => {
            cancel = true
        }
    }, [contactSyncKey, delivery.method, delivery.pickupLocationId, contact.customerId])

    /**
     * Soft one-way mirror: while creating a new customer (no contact/shipping customer
     * selected), keep shipping person fields in sync with contact until the user edits
     * them on shipping (or picks a shipping customer).
     */
    useEffect(() => {
        if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
            softContactPersonMirrorActiveRef.current = false
            return
        }
        const softMirror =
            contact.customerId == null && value.shippingCustomerId == null
        if (!softMirror) {
            softContactPersonMirrorActiveRef.current = false
            return
        }

        const from = contactPersonMirrorValues(contact)
        if (!softContactPersonMirrorActiveRef.current) {
            softContactPersonMirrorActiveRef.current = true
            const ship = valueRef.current
            for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
                const shipVal = String(ship[key] ?? "")
                const contactVal = String(from[key] ?? "")
                if (shipVal !== "" && shipVal !== contactVal) {
                    contactPersonOverwriteKeysRef.current.add(key)
                }
            }
        }

        onChange((prev) => {
            let changed = false
            const next = { ...prev }
            for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
                if (contactPersonOverwriteKeysRef.current.has(key)) {
                    continue
                }
                const nextVal = from[key] ?? ""
                if (String(next[key] ?? "") !== String(nextVal)) {
                    next[key] = nextVal
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [
        contact.customerId,
        contact.firstName,
        contact.lastName,
        contact.companyName,
        contact.phone,
        contact.email,
        value.shippingCustomerId,
        delivery.method,
        delivery.pickupLocationId,
    ])

    useEffect(() => {
        // Ensure manual overrides / autofill stay formatted consistently.
        const raw = value.phone ?? ""
        const formatted = formatPhone(raw)
        if (formatted && formatted !== raw) {
            onChange((p) => ({ ...p, phone: formatted }))
        }

    }, [value.phone])

    useEffect(() => {
        if (
            !isActive ||
            activationFocusMode == null ||
            !onActivationFocusConsumed
        ) {
            return
        }
        const mode = activationFocusMode
        const tmr = window.setTimeout(() => {
            if (mode === "first") {
                focusElement(
                    document.getElementById("shipping-customer-search-query"),
                    {
                        enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
                        delay: true,
                    }
                )
                onActivationFocusConsumed()
                return
            }
            if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
                setEmphasizeNext(true)
                focusOrderFormNextButton(nextButtonRef)
                onActivationFocusConsumed()
                return
            }
            const prioritizeSearch =
                contact.customerId != null &&
                (contact.default_ship_id == null || contact.ship_to == null)
            if (prioritizeSearch) {
                setEmphasizeNext(false)
                setShippingSearchFocusNonce((n) => n + 1)
                onActivationFocusConsumed()
                return
            }
            const v = valueRef.current
            if (isShippingAddressComplete(v)) {
                setEmphasizeNext(true)
                focusOrderFormNextButton(nextButtonRef)
            } else {
                setEmphasizeNext(false)
                const id = firstMissingShippingFieldId(v)
                focusElement(document.getElementById(id), { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            }
            onActivationFocusConsumed()
        }, activationFocusDelayMs)
        return () => window.clearTimeout(tmr)
    }, [
        isActive,
        activationFocusMode,
        activationFocusDelayMs,
        onActivationFocusConsumed,
        delivery.method,
        delivery.pickupLocationId,
        contact.customerId,
        contact.default_ship_id,
        contact.ship_to,
    ])

    async function resetToMainCustomer() {
        const cid = contact.customerId
        if (cid == null) {
            return
        }
        const fields = await shippingFieldsFromShipTo(
            contact.ship_to ?? null,
            contactLikeFromProps(contact)
        )
        onChange((prev) =>
            applyShippingCustomerLoad(
                prev,
                fields,
                cid,
                contact.default_ship_id ?? null
            )
        )
    }

    function handleShippingCustomerSelect(c) {
        ; (async () => {
            const fields = await shippingFieldsFromApiCustomer(c)
            const merged = applyShippingCustomerLoad(
                valueRef.current,
                fields,
                c.id ?? null,
                c.default_ship_id ?? null
            )
            onChange(() => merged)
            window.requestAnimationFrame(() => {
                if (isShippingAddressComplete(merged)) {
                    setEmphasizeNext(true)
                    focusOrderFormNextButton(nextButtonRef)
                } else {
                    setEmphasizeNext(false)
                    document
                        .getElementById(firstMissingShippingFieldId(merged))
                        ?.focus({ preventScroll: true })
                }
            })
        })()
    }

    function patchField(patch) {
        onChange((prev) => {
            const hasSelected =
                prev.shippingCustomerId != null
            if (
                contact.customerId == null &&
                prev.shippingCustomerId == null
            ) {
                for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
                    if (Object.prototype.hasOwnProperty.call(patch, key)) {
                        contactPersonOverwriteKeysRef.current.add(key)
                    }
                }
            }
            return {
                ...prev,
                ...patch,
                addressDirty:
                    hasSelected && Object.keys(patch).length > 0 ?
                        true :
                        prev.addressDirty,
            }
        })
    }

    function undoField(key) {
        onChange((prev) => {
            if (
                contact.customerId == null &&
                prev.shippingCustomerId == null
            ) {
                contactPersonOverwriteKeysRef.current.delete(key)
                const from = contactPersonMirrorValues(contact)
                return { ...prev, [key]: from[key] ?? "" }
            }
            const next = undoAddressField(prev, prev.addressBaseline, key)
            if (!addressHasEdits(next, next.addressBaseline)) {
                next.addressDirty = false
            }
            return next
        })
    }

    function applyFieldToContact(key) {
        if (onContactChange == null) {
            return
        }
        const raw = valueRef.current[key] ?? ""
        const nextVal = key === "phone" ? (formatPhone(raw) || raw) : raw
        contactPersonOverwriteKeysRef.current.delete(key)
        onContactChange((prev) => ({ ...prev, [key]: nextVal }))
    }

    const softContactPersonMirror =
        contact.customerId == null && value.shippingCustomerId == null
    const addressBaseline = value.addressBaseline ?? null
    const contactPersonBaseline = useMemo(
        () =>
            softContactPersonMirror ? contactPersonMirrorValues(contact) : null,
        [
            softContactPersonMirror,
            contact.firstName,
            contact.lastName,
            contact.companyName,
            contact.phone,
            contact.email,
        ]
    )
    const fieldDiffs = useMemo(
        () => {
            if (value.shippingCustomerId != null) {
                return getAddressFieldDiffs(value, addressBaseline)
            }
            if (softContactPersonMirror) {
                return getAddressPersonFieldDiffs(value, contactPersonBaseline)
            }
            return []
        },
        [
            value,
            addressBaseline,
            softContactPersonMirror,
            contactPersonBaseline,
        ]
    )
    const diffsByKey = useMemo(() => {
        /** @type {Record<string, (typeof fieldDiffs)[number]>} */
        const map = {}
        for (const d of fieldDiffs) {
            map[d.key] = d
        }
        return map
    }, [fieldDiffs])
    const showUpdateDefaultPrompt =
        value.shippingCustomerId != null && fieldDiffs.length > 0
    const showSetDefaultPrompt =
        value.shippingCustomerId == null && addressHasAnyDetail(value)

    function labelHint(key) {
        const d = diffsByKey[key]
        if (!d) {
            return null
        }
        return (
            <FieldChangeHint
                fromDisplay={d.fromDisplay}
                onUndo={() => undoField(key)}
                {...(softContactPersonMirror && onContactChange != null ?
                    {
                        applyLabel: "Apply to contact",
                        onApply: () => applyFieldToContact(key),
                    } :
                    {})}
            />
        )
    }

    if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
        return (
            <div className="row g-4">
                <div className="col-12">
                    <div
                        className="p-3 rounded d-flex align-items-start"
                        style={{ backgroundColor: "#e7f3ff", gap: "12px" }}
                    >
                        <MapPin className="text-primary mt-1" size={20} />
                        <div>
                            <h3
                                className="fw-semibold"
                                style={{
                                    color: "#1e3a8a",
                                    fontSize: "1rem",
                                    marginBottom: "4px",
                                }}
                            >
                                Pickup Location Selected
                            </h3>
                            <p className="mb-0" style={{ color: "#1e40af", fontSize: "0.875rem" }}>
                                Your order will be available for pickup at our{" "}
                                selected pickup location.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="d-flex justify-content-end">
                        <button
                            ref={nextButtonRef}
                            type="button"
                            className="btn btn-primary px-4"
                            style={
                                emphasizeNext ?
                                    {
                                        boxShadow:
                                            "0 0 0 0.25rem rgba(13, 110, 253, 0.45)",
                                    } :
                                    undefined
                            }
                            onClick={onNext}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Accordion
            title={title}
            isActive={isOpen ?? isActive}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            disabled={disabled}
            onClick={onHeaderClick}
            hasErrors={hasErrors}
        >
            <div className="row g-4">
                <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: "#e7f3ff" }}>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <h3
                                className="fw-semibold mb-0 d-flex align-items-center"
                                style={{ color: "#1e3a8a", fontSize: "1rem" }}
                            >
                                <User className="me-2" size={20} />
                                Load Shipping Address from Customer
                            </h3>
                            {shippingAlignedWithMainCustomer ? (
                                <span
                                    className="text-end"
                                    style={{
                                        color: "#1e40af",
                                        maxWidth: "20rem",
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Auto-filling from{" "}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 align-baseline border-0"
                                        style={{
                                            color: "#1e40af",
                                            fontSize: "inherit",
                                            textDecoration: "underline",
                                        }}
                                        onClick={() => onJumpToSection?.(0)}
                                    >
                                        Contact Information
                                    </button>
                                </span>
                            ) : showResetToMainCustomer ? (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 border-0 shipping-reset-main-customer-btn"
                                    onClick={() => resetToMainCustomer()}
                                >
                                    Reset to main customer
                                </button>
                            ) : null}
                        </div>
                        <CustomerSearchInput
                            idPrefix="shipping-customer-search"
                            placeholder="Search customer for shipping address..."
                            selectedCustomerId={/** @type {number | null} */ (value.shippingCustomerId ?? null)}
                            selectedDisplayLabel={
                                contact.customerId != null &&
                                    value.shippingCustomerId != null &&
                                    Number(value.shippingCustomerId) ===
                                    Number(contact.customerId)
                                    ? mainCustomerDisplayLabel
                                    : null
                            }
                            focusNonce={shippingSearchFocusNonce}
                            onClearSelection={() => {
                                resetContactPersonSoftMirror()
                                onChange((prev) => clearShippingCustomer(prev))
                            }}
                            onSelect={handleShippingCustomerSelect}
                            onSearchQueryChange={setShippingSearchQuery}
                        />
                    </div>
                </div>

                <div className="col-12 mb-3">
                    <label className="form-label">
                        Country<span className="text-danger ms-1">*</span>
                        {labelHint("country")}
                    </label>
                    <select
                        id="shipping-country"
                        value={String(value.country ?? "")}
                        onChange={(e) => patchField({ country: e.target.value })}
                        className={`form-select${showFieldErrors && fieldErrors?.country ? " is-invalid" : ""}`}
                    >
                        <option value="">Select a country</option>
                        {countryNames.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                    {showFieldErrors && fieldErrors?.country ? (
                        <div className="text-danger small mt-1">{fieldErrors.country}</div>
                    ) : null}
                </div>

                <div className="col-12 col-md-6">
                    <InputField
                        id="shipping-first-name"
                        label="First Name"
                        required
                        value={value.firstName}
                        labelExtra={labelHint("firstName")}
                        error={nameHasComma(value.firstName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.firstName : undefined}
                        onChange={(e) => patchField({ firstName: e.target.value })}
                    />
                </div>
                <div className="col-12 col-md-6">
                    <InputField
                        id="shipping-last-name"
                        label="Last Name"
                        required
                        value={value.lastName}
                        labelExtra={labelHint("lastName")}
                        error={nameHasComma(value.lastName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.lastName : undefined}
                        onChange={(e) => patchField({ lastName: e.target.value })}
                    />
                </div>

                <div className="col-12">
                    <InputField
                        id="shipping-company-name"
                        label="Company Name"
                        value={value.companyName}
                        labelExtra={labelHint("companyName")}
                        error={showFieldErrors ? fieldErrors?.companyName : undefined}
                        onChange={(e) => patchField({ companyName: e.target.value })}
                    />
                </div>

                <div className="col-12 col-md-6 mb-3">
                    <label className="form-label">
                        {value.country === "United States" ? "State" : "Province"}{" "}
                        <span className="text-danger ms-1">*</span>
                        {labelHint("province")}
                    </label>
                    <select
                        id="shipping-province"
                        value={String(value.province ?? "")}
                        onChange={(e) => patchField({ province: e.target.value })}
                        className={`form-select${showFieldErrors && fieldErrors?.province ? " is-invalid" : ""}`}
                    >
                        <option value="">
                            {value.country === "United States"
                                ? "Select a state"
                                : "Select a province"}
                        </option>
                        {regionOptions.map((s) => (
                            <option key={s.state_code} value={s.state_code}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                    {showFieldErrors && fieldErrors?.province ? (
                        <div className="text-danger small mt-1">{fieldErrors.province}</div>
                    ) : null}
                </div>
                <div className="col-12 col-md-6">
                    <InputField
                        id="shipping-city"
                        label="City"
                        required
                        value={value.city}
                        labelExtra={labelHint("city")}
                        error={showFieldErrors ? fieldErrors?.city : undefined}
                        onChange={(e) => patchField({ city: e.target.value })}
                    />
                </div>

                <div className="col-12">
                    <InputField
                        id="shipping-street"
                        label="Street Address"
                        required
                        value={value.streetAddress}
                        labelExtra={labelHint("streetAddress")}
                        error={showFieldErrors ? fieldErrors?.streetAddress : undefined}
                        onChange={(e) => patchField({ streetAddress: e.target.value })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="shipping-postal"
                        label="Postal Code"
                        required
                        value={value.postalCode}
                        labelExtra={labelHint("postalCode")}
                        error={showFieldErrors ? fieldErrors?.postalCode : undefined}
                        onChange={(e) => patchField({ postalCode: e.target.value })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="shipping-phone"
                        label="Phone"
                        required
                        value={value.phone}
                        labelExtra={labelHint("phone")}
                        error={showFieldErrors ? fieldErrors?.phone : undefined}
                        onChange={(e) => patchField({ phone: formatPhone(e.target.value) })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="shipping-email"
                        label="Email"
                        value={value.email}
                        labelExtra={labelHint("email")}
                        error={showFieldErrors ? fieldErrors?.email : undefined}
                        onChange={(e) => patchField({ email: e.target.value })}
                    />
                </div>

                {showUpdateDefaultPrompt || showSetDefaultPrompt ? (
                    <div className="col-12">
                        <CustomerChoicePrompt
                            id="shipping-attach-address-to-customer"
                            message={
                                showUpdateDefaultPrompt ?
                                    "Also update the customer default shipping address to the above?" :
                                    "Set this as the customer's default shipping address for future orders?"
                            }
                            checked={Boolean(value.attachAddressToCustomer)}
                            onCheckedChange={(checked) =>
                                onChange((prev) => ({
                                    ...prev,
                                    attachAddressToCustomer: checked,
                                }))
                            }
                        />
                    </div>
                ) : null}

                <div className="col-12">
                    <div className="d-flex justify-content-end">
                        <button
                            ref={nextButtonRef}
                            type="button"
                            className="btn btn-primary px-4"
                            style={
                                emphasizeNext ?
                                    {
                                        boxShadow:
                                            "0 0 0 0.25rem rgba(13, 110, 253, 0.45)",
                                    } :
                                    undefined
                            }
                            onClick={onNext}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </Accordion>
    )
}
